import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useToast } from '@/components/Toast';
import { PasswordInput } from '@/components/PasswordInput';
import { AuthLayout } from './AuthLayout';
import {
  MIN_PASSWORD_LENGTH as MIN_LENGTH,
  isPasswordAcceptable,
  passwordProblems,
} from '@/lib/password';
import type { Me } from '@/lib/types';

/**
 * FE-1-9 — one component serving both invite-accept and password-reset. The two
 * flows differ only in endpoint and copy; splitting them would duplicate the
 * password rules and let the two drift.
 */
export function SetPasswordPage({ mode }: { mode: 'invite' | 'reset' }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { adopt } = useAuth();
  const toast = useToast();

  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copy =
    mode === 'invite'
      ? {
          title: 'Set your password',
          subtitle: "You've been enrolled in Ambition. Choose a password to get started.",
          submit: 'Set password and continue',
          endpoint: '/auth/invite/accept',
        }
      : {
          title: 'Choose a new password',
          subtitle: 'Pick something you have not used before.',
          submit: 'Update password',
          endpoint: '/auth/password/reset',
        };

  // One shared policy with the server, so the button cannot enable for something the
  // API will reject — including the blocked-fragment rule this screen used to omit.
  const problems = useMemo(
    () => passwordProblems(password, { confirmation: confirm }),
    [password, confirm],
  );

  const canSubmit =
    token.length > 0 && isPasswordAcceptable(password, { confirmation: confirm }) && !submitting;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'invite') {
        // Accepting an invite signs the user straight in — a second manual login
        // here would be pure friction.
        const me = await api<Me>(copy.endpoint, { method: 'POST', body: { token, password } });
        adopt(me);
        toast.success('Welcome to Ambition.');
        navigate(me.landingPath, { replace: true });
      } else {
        // A reset revokes every existing session server-side, so the user must
        // sign in again with the new password.
        await api(copy.endpoint, { method: 'POST', body: { token, password } });
        toast.success('Password updated. Please sign in.');
        navigate('/login', { replace: true });
      }
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout
        title="That link is not valid"
        subtitle="The link may have been truncated by your email client."
        footer={
          <Link to="/login" className="font-medium text-accent hover:underline">
            Back to sign in
          </Link>
        }
      >
        <p className="text-sm text-muted">
          {mode === 'invite'
            ? 'Ask your programme admin to resend your invitation.'
            : 'Request a new password reset link and try again.'}
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={copy.title}
      subtitle={copy.subtitle}
      footer={
        <Link to="/login" className="font-medium text-accent hover:underline">
          Back to sign in
        </Link>
      }
    >
      {error && (
        <div
          className="mb-5 rounded-control border border-[#e0796b]/45 bg-[#e0796b]/12 px-3.5 py-3 text-sm text-ink"
          role="alert"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-4">
          <label className="label" htmlFor="password">
            New password
          </label>
          <PasswordInput
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            aria-describedby="password-rules"
          />
          <p id="password-rules" className="mt-1.5 text-xs text-muted">
            At least {MIN_LENGTH} characters, including a letter and a number.
          </p>
        </div>

        <div className="mb-4">
          <label className="label" htmlFor="confirm">
            Confirm password
          </label>
          <PasswordInput
            id="confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        {problems.length > 0 && (
          <ul className="mb-4 space-y-1" aria-live="polite">
            {problems.map((problem) => (
              <li key={problem} className="text-xs text-secondary">
                • {problem}
              </li>
            ))}
          </ul>
        )}

        <button type="submit" disabled={!canSubmit} className="btn-primary w-full">
          {submitting ? 'Saving…' : copy.submit}
        </button>
      </form>
    </AuthLayout>
  );
}

/** §5.2 — never reveals whether an address is on file. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    try {
      await api('/auth/password/forgot', { method: 'POST', body: { email } });
    } catch {
      // Deliberately ignored: the confirmation must look identical whether or not
      // the account exists, including when the send itself fails.
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle={sent ? undefined : 'We will email you a link to choose a new one.'}
      footer={
        <Link to="/login" className="font-medium text-accent hover:underline">
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <div role="status">
          <p className="text-sm text-ink">
            If <span className="font-medium">{email}</span> belongs to an Ambition account, a reset
            link is on its way.
          </p>
          <p className="mt-3 text-sm text-muted">
            The link works once and expires in 30 minutes. Check your spam folder if it has not
            arrived in a few minutes.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-4">
            <label className="label" htmlFor="email">
              Work email
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              placeholder="you@company.com"
            />
          </div>
          <button type="submit" disabled={submitting || !email} className="btn-primary w-full">
            {submitting ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
